/**
 * VisualizerBars — 20 phosphor bars that dance while the stream is playing.
 *
 * Heights are generated once per mount instead of per render, so the animation
 * stays smooth and the bars do not teleport when metadata polling re-renders.
 */

import React, { useMemo } from 'react';
import { motion } from 'framer-motion';

const BAR_COUNT = 20;

function reducedMotion() {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches)
  );
}

export default function VisualizerBars({ isPlaying, volume = 80, isMuted = false }) {
  const calm = reducedMotion();
  const active = isPlaying && !isMuted && volume > 0;
  const scale = Math.max(0.2, Math.min(1, volume / 100));

  const bars = useMemo(
    () =>
      Array.from({ length: BAR_COUNT }, (_, index) => ({
        base: [
          Math.random() * 30 + 10,
          Math.random() * 80 + 20,
          Math.random() * 40 + 15,
        ],
        duration: 0.3 + Math.random() * 0.2,
        delay: index * 0.05,
      })),
    [],
  );

  return (
    <div
      className="flex h-16 items-end justify-center gap-1 opacity-70"
      role="img"
      aria-label={active ? 'Audio visualizer active' : 'Audio visualizer idle'}
    >
      {bars.map((bar, index) => (
        <motion.div
          key={index}
          className="dmr-bar"
          style={{ height: '10%' }}
          animate={
            calm || !active
              ? { height: '10%' }
              : {
                  height: bar.base.map((pct) => `${Math.max(10, Math.round(pct * scale))}%`),
                }
          }
          transition={
            calm || !active
              ? { duration: 0.3 }
              : {
                  duration: bar.duration,
                  repeat: Infinity,
                  repeatType: 'reverse',
                  ease: 'easeInOut',
                  delay: bar.delay,
                }
          }
        />
      ))}
    </div>
  );
}
